import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Formik, Form } from "formik";
import logo from "../assets/logo.svg";
import seasonConfig from "../data/season.config.json";
import { Sun, Moon, AlertCircle, ChevronLeft, ChevronRight, Send, Check, Tag, Sparkles } from "lucide-react";
import Confetti from "react-confetti";

import { useTheme } from "../Providers/ThemeContext";
import { getValidationSchema } from "../services/validationSchema";
import { StepIdentite } from "../Step/Identite";
import { StepCoordonnees } from "../Step/Coordonnees";
import { StepMobiliteSante } from "../Step/MobiliteSante";
import { Step2 as StepAssociation } from "../Step/AssociationInfo";
import { Step3 as StepCotisation } from "../Step/MembershipFee";
import { StepLegal } from "../Step/Legal";
import { Step4 as StepConfirmation } from "../Step/Confirmation";
import CookieBanner from "../components/CookiesBanners";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { submitForm } from "../services/services";
import Confirmed from "../Step/Confirmed";
import { cn } from "../lib/utils";

/* ── Confettis via portal ── */
function ConfettiPortal() {
  const [active, setActive] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setActive(false), 7000);
    return () => clearTimeout(t);
  }, []);

  if (!active) return null;

  return ReactDOM.createPortal(
    <Confetti
      width={window.innerWidth}
      height={window.innerHeight}
      numberOfPieces={350}
      recycle={false}
      colors={["#609BAB", "#E87E58", "#ffffff", "#ffd700", "#a78bfa", "#34d399"]}
      style={{ position: "fixed", top: 0, left: 0, zIndex: 9999, pointerEvents: "none" }}
    />,
    document.body
  );
}

// 7 étapes courtes plutôt que 4 étapes denses — chaque écran doit tenir
// en 100vh sans scroll, la Cotisation est sautée pour les convié/bénévoles.
const STEPS_CONFIG = [
  { id: 1, label: "Identité" },
  { id: 2, label: "Coordonnées" },
  { id: 3, label: "Santé" },
  { id: 4, label: "Association" },
  { id: 5, label: "Cotisation" },
  { id: 6, label: "Légal" },
  { id: 7, label: "Signature" },
];

/* ══════════════════════════════════════════════════════
   FORM MANAGER
══════════════════════════════════════════════════════ */
const FormManager = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [isInvited, setIsInvited]     = useState(false);
  const [isBenevole, setIsBenevole]   = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmed, setConfirmed]     = useState(false);
  const { theme, toggleTheme }        = useTheme();

  const allSteps      = [1, 2, 3, 4, 5, 6, 7];
  const filteredSteps = isInvited ? allSteps.filter((s) => s !== 5) : allSteps;
  const visibleConfig = STEPS_CONFIG.filter((s) => !isInvited || s.id !== 5);
  const totalSteps    = 7;

  const currentIndex = filteredSteps.indexOf(currentStep);

  const nextStep = () => { const n = filteredSteps[currentIndex + 1]; if (n) setCurrentStep(n); };
  const prevStep = () => { const p = filteredSteps[currentIndex - 1]; if (p !== undefined) setCurrentStep(p); };
  const goToStep = (s)  => { if (filteredSteps.includes(s)) setCurrentStep(s); };

  const handleSubmit = async (values) => {
    try {
      await submitForm(values, isInvited, isBenevole);
      setCurrentStep(8);
      setConfirmed(true);
    } catch (err) {
      console.error("Failed to submit form:", err);
    }
  };

  // Deux façons d'arriver en mode "convié" (pas de cotisation, gratuit) :
  // l'ancien lien à token fixe, ou juste ?benevole (présence du paramètre,
  // peu importe sa valeur) — plus simple à partager pour les bénévoles,
  // pas besoin d'un lien à part. isBenevole reste distinct d'isInvited :
  // le règlement des bénévoles ne doit s'afficher que pour ?benevole,
  // pas pour un convié "normal" via le token.
  useEffect(() => {
    const benevole = searchParams.has("benevole");
    if (token === "bWVtYnJlY29udmllcg==" || benevole) setIsInvited(true);
    if (benevole) setIsBenevole(true);
  }, [token, searchParams]);

  /* ══ RENDER ══ */
  return (
    <div
      className="h-[100dvh] w-full overflow-hidden relative"
      style={{ background: theme === "dark" ? "hsl(var(--background))" : "#faf8f4" }}
    >
      {/* Décor : grille + halos + filigrane + coins — voir index.css */}
      <div className="page-deco">
        <span className="page-glow-1" />
        <span className="page-glow-2" />
        <img className="page-mark" src={logo} alt="" aria-hidden="true" />
        <span className="page-corner page-corner--tl" />
        <span className="page-corner page-corner--br" />
      </div>

      {confirmed && <ConfettiPortal />}

      <div className="mx-auto w-full relative" style={{ maxWidth: 1100, padding: "16px 24px", zIndex: 1 }}>

        {/* ══ TOP BAR ══ */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <img src={logo} alt="ImagiGeeks" className="w-6 h-6" />
            <span className="text-sm font-bold">ImagiGeeks</span>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="btn btn-ghost"
            style={{ padding: 8, borderRadius: 99 }}
            aria-label="Changer le thème"
          >
            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/* ══ TITRE (compact — la hauteur est comptée) ══ */}
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-2"
          style={{ background: "var(--acc-soft)", border: "1px solid var(--acc-border)" }}
        >
          <Sparkles className="h-3 w-3" style={{ color: "var(--acc)" }} />
          <span className="eyebrow" style={{ fontSize: 10.5 }}>
            Association loi 1901 · Saison {seasonConfig.currentSeason}
          </span>
          {isInvited && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide"
              style={{ background: "var(--req-soft)", color: "var(--req)", border: "1px solid var(--req-border)" }}
            >
              <Tag className="h-2.5 w-2.5" />
              Convié
            </span>
          )}
        </div>
        <h1 className="font-almendra m-0 mb-3" style={{ fontSize: "clamp(22px,3.2vw,30px)", fontWeight: 700 }}>
          {isInvited ? "Formulaire " : "Rejoindre "}
          <span style={{ color: "var(--acc)" }}>{isInvited ? "convié" : "ImagiGeeks"}</span>
        </h1>

        {/* ══ STEPPER ══ */}
        {!confirmed && (
          <div className="flex items-center mb-3">
            {visibleConfig.map((step, idx) => {
              const isDone    = idx < currentIndex;
              const isCurrent = step.id === currentStep;
              return (
                <div key={step.id} className="contents">
                  <button
                    type="button"
                    onClick={() => goToStep(step.id)}
                    className="flex flex-shrink-0 items-center gap-1.5"
                    aria-label={step.label}
                  >
                    <div className={cn("step-num", isCurrent && "cur", isDone && "done")} style={{ width: 26, height: 26, fontSize: 10 }}>
                      {isDone ? <Check className="h-3 w-3" /> : String(idx + 1).padStart(2, "0")}
                    </div>
                    <span
                      className="hidden md:inline text-[11px] font-semibold"
                      style={{ color: isCurrent ? "hsl(var(--foreground))" : "hsl(var(--foreground) / 0.45)" }}
                    >
                      {step.label}
                    </span>
                  </button>
                  {idx < visibleConfig.length - 1 && (
                    <div className={cn("step-bar mx-1.5 md:mx-2.5", idx < currentIndex && "done")} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ══ CARD — hauteur naturelle visée, scroll interne discret en filet de sécurité ══ */}
        <div
          className="card-flat step-scroll overflow-y-auto"
          style={{ padding: "clamp(12px,2.5vw,20px)", maxHeight: "calc(100dvh - 170px)" }}
        >
          <Formik
            initialValues={{
              nom: "", prenom: "", pseudo: "", dateNaissance: "",
              adressePostale: "", zipCode: "", city: "",
              email: "", telephone: "",
              allergies: "", notes: "", pmr: false,
              idDiscord: "", domainePratique: [], motivation: "",
              cosplay: "", cultureGeek: "", cultureImaginaire: "",
              statut: "", modePaiement: "",
              reglementInterieurLu: false, imageRightsConsent: false, dateSignature: "",
              vehicule: false,
            }}
            validationSchema={getValidationSchema(isInvited)}
            onSubmit={async (values, { setSubmitting }) => {
              await handleSubmit(values);
              setSubmitting(false);
            }}
          >
            {({ errors, isSubmitting, values }) => (
              <Form>
                <div key={currentStep} className="step-enter">
                  {currentStep === 1 && <StepIdentite />}
                  {currentStep === 2 && <StepCoordonnees />}
                  {currentStep === 3 && <StepMobiliteSante />}
                  {currentStep === 4 && <StepAssociation />}
                  {currentStep === 5 && !isInvited && <StepCotisation />}
                  {currentStep === 6 && <StepLegal isBenevole={isBenevole} />}
                  {currentStep === 7 && <StepConfirmation values={values} isInvited={isInvited} />}
                  {confirmed && <Confirmed values={values} />}
                </div>

                {!confirmed && (
                  <div
                    className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3"
                    style={{ borderTop: "1px solid var(--line)" }}
                  >
                    <span className="text-xs" style={{ color: "hsl(var(--foreground) / 0.4)" }}>
                      <span style={{ color: "var(--req)" }} className="font-bold">*</span> Champs requis
                    </span>

                    <div className="flex items-center gap-2 ml-auto">
                      {currentStep > 1 && (
                        <button type="button" onClick={prevStep} className="btn btn-ghost" aria-label="Précédent">
                          <ChevronLeft className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Précédent</span>
                        </button>
                      )}

                      {currentStep < totalSteps ? (
                        <button type="button" onClick={nextStep} className="btn btn-acc" aria-label="Suivant">
                          <span className="hidden sm:inline">Suivant</span>
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          {Object.keys(errors).length > 0 && (
                            <p className="field-err flex items-center gap-1 m-0">
                              <AlertCircle className="h-3 w-3 shrink-0" />
                              <span className="hidden sm:inline">Corrigez les erreurs.</span>
                            </p>
                          )}
                          <button
                            type="submit"
                            disabled={isSubmitting || Object.keys(errors).length > 0}
                            className="btn btn-req"
                          >
                            <Send className="h-3.5 w-3.5" />
                            Soumettre
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <CookieBanner />
    </div>
  );
};

export default FormManager;
